import { useApi } from '@classgo/app-kit';
import type { GuardianItem, GuardianListItem } from '@classgo/shared';
import { linkGuardianInputSchema, updateGuardianLinkInputSchema } from '@classgo/shared';
import { Button, Checkbox, Input } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi, peopleQueries } from '../people/api';
import { FormShell } from './form-shell';
import { GuardianForm } from './guardian-form';

/**
 * Links a guardian to a student: search an existing one or create it inline, then the
 * relationship and the two flags. Editing an existing link changes only the latter.
 */
export const LinkForm = ({
  studentId,
  current,
  onDone,
}: {
  studentId: string;
  current: GuardianItem | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ id: string; label: string } | null>(
    current
      ? { id: current.id, label: `${current.givenName ?? ''} ${current.familyName ?? ''}`.trim() }
      : null,
  );
  const [creating, setCreating] = useState(false);
  const found = useQuery({
    ...peopleQueries.guardians(api, { search: search.trim() }),
    enabled: !current && search.trim().length >= 2,
  });
  const form = useForm({
    schema: current ? updateGuardianLinkInputSchema : linkGuardianInputSchema,
    initial: {
      relationship: current?.relationship ?? 'acudiente',
      isPrimary: current?.isPrimary ?? false,
      billingResponsible: current?.billingResponsible ?? false,
    },
    toInput: (values) => (current ? values : { guardianUserId: selected?.id ?? '', ...values }),
  });
  if (creating) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{t('link.creating')}</p>
        <GuardianForm
          current={null}
          onDone={(id) => {
            setSelected({ id, label: t('link.created') });
            setCreating(false);
          }}
        />
        <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      submitLabel={current ? t('common.save') : t('link.submit')}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await peopleApi(api).updateLink(studentId, current.id, input as never);
            else await peopleApi(api).linkGuardian(studentId, input as never);
          })
          .then((ok) => ok && onDone());
      }}
    >
      {!current ? (
        <div className="space-y-2">
          <Input
            label={t('link.search')}
            help={t('link.search.help')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelected(null);
            }}
          />
          {selected ? (
            <p className="text-sm text-success-700">
              {t('link.selected', { name: selected.label })}
            </p>
          ) : found.data && found.data.items.length > 0 ? (
            <ul className="max-h-40 divide-y divide-gray-100 overflow-auto rounded-control border border-gray-200 text-sm">
              {found.data.items.map((guardian: GuardianListItem) => (
                <li key={guardian.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                    onClick={() =>
                      setSelected({
                        id: guardian.id,
                        label: `${guardian.givenName ?? ''} ${guardian.familyName ?? ''}`.trim(),
                      })
                    }
                  >
                    <span>
                      {guardian.givenName} {guardian.familyName}
                      <span className="ml-2 text-xs text-gray-500">
                        {guardian.email ?? guardian.documentNumber ?? ''}
                      </span>
                    </span>
                    <span className="text-xs text-gray-500">
                      {t('link.students', { count: guardian.studentsCount })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : search.trim().length >= 2 && found.isSuccess ? (
            <p className="text-sm text-gray-500">{t('link.none')}</p>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
            {t('link.create')}
          </Button>
        </div>
      ) : null}
      <Input
        label={t('link.relationship')}
        help={t('link.relationship.help')}
        value={form.values.relationship}
        onChange={(event) => form.set('relationship', event.target.value)}
        {...(form.fieldError('relationship') ? { error: form.fieldError('relationship') } : {})}
      />
      <Checkbox
        label={t('student.guardian.primary')}
        description={t('link.primary.help')}
        checked={form.values.isPrimary}
        onChange={(event) => form.set('isPrimary', event.target.checked)}
      />
      <Checkbox
        label={t('student.guardian.billing')}
        description={t('link.billing.help')}
        checked={form.values.billingResponsible}
        onChange={(event) => form.set('billingResponsible', event.target.checked)}
      />
    </FormShell>
  );
};
